import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { siteConfig, legalLine } from '../siteConfig';
import {
  navLinks,
  serviceLinks,
  industryLinks,
  customSoftwareLinks,
  companyLinks,
  legalLinks,
} from '../data/siteData';
import { ButtonLink, Container } from './SiteUi';

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
};

const Dropdown = ({ item }) => (
  <div className="group relative">
    <button type="button" className="flex items-center gap-1.5 py-2 text-sm text-dust transition-colors hover:text-gold-light">
      {item.label}
      <span className="text-[10px] text-ash">▾</span>
    </button>
    <div className="invisible absolute left-0 top-full z-50 w-60 translate-y-1 rounded-2xl border border-white/[0.08] bg-ink-light/95 p-2 opacity-0 shadow-2xl shadow-ink/40 backdrop-blur-xl transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
      {item.children.map((child) => (
        <Link key={child.to} to={child.to} className="block rounded-xl px-3 py-2.5 text-sm text-dust transition-colors hover:bg-white/[0.04] hover:text-gold-light">
          {child.label}
        </Link>
      ))}
    </div>
  </div>
);

export default function SiteLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const demoHref = siteConfig.demoPhone ? `tel:${siteConfig.demoPhone.replace(/\s+/g, '')}` : null;

  return (
    <div className="min-h-screen bg-ink text-cream">
      <ScrollToTop />

      <header className="sticky top-0 z-40 border-b border-white/[0.05] bg-ink/80 backdrop-blur-xl">
        <Container className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="font-display text-lg font-semibold tracking-wide text-cream">
            Cloud<span className="text-gold-light">Next</span>
          </Link>

          <nav className="hidden items-center gap-6 lg:flex" aria-label="Main navigation">
            {navLinks.map((item) => (
              item.children ? (
                <Dropdown key={item.label} item={item} />
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `py-2 text-sm transition-colors ${isActive ? 'text-gold-light' : 'text-dust hover:text-gold-light'}`}
                >
                  {item.label}
                </NavLink>
              )
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <ButtonLink to="/contact" variant="secondary" size="sm">Book a call</ButtonLink>
            {demoHref ? (
              <ButtonLink href={demoHref} size="sm">Hear the demo</ButtonLink>
            ) : (
              <ButtonLink to="/demo" size="sm">Hear the demo</ButtonLink>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-sm text-cream lg:hidden"
            aria-expanded={menuOpen}
            aria-label="Toggle menu"
          >
            {menuOpen ? 'Close' : 'Menu'}
          </button>
        </Container>

        {menuOpen ? (
          <div className="border-t border-white/[0.05] bg-ink-light lg:hidden">
            <Container className="grid gap-6 py-6">
              <div>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-ash">Services</p>
                <div className="mt-2 grid gap-1">
                  {serviceLinks.map((link) => (
                    <Link key={link.to} to={link.to} className="rounded-xl px-2 py-2 text-sm text-dust hover:bg-white/[0.04] hover:text-gold-light">{link.label}</Link>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-ash">Industries</p>
                <div className="mt-2 grid gap-1">
                  {industryLinks.map((link) => (
                    <Link key={link.to} to={link.to} className="rounded-xl px-2 py-2 text-sm text-dust hover:bg-white/[0.04] hover:text-gold-light">{link.label}</Link>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-ash">Company</p>
                <div className="mt-2 grid gap-1">
                  {[...customSoftwareLinks.slice(0, 1), ...companyLinks, { label: 'Pricing', to: '/pricing' }, { label: 'Work', to: '/work' }].map((link) => (
                    <Link key={link.to} to={link.to} className="rounded-xl px-2 py-2 text-sm text-dust hover:bg-white/[0.04] hover:text-gold-light">{link.label}</Link>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <ButtonLink to="/contact" size="sm">Book a call</ButtonLink>
                <ButtonLink to="/demo" variant="secondary" size="sm">Hear the demo</ButtonLink>
              </div>
            </Container>
          </div>
        ) : null}
      </header>

      <main>{children}</main>

      <footer className="border-t border-white/[0.05] bg-ink-light">
        <Container className="py-14">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <Link to="/" className="font-display text-lg font-semibold tracking-wide text-cream">
                Cloud<span className="text-gold-light">Next</span>
              </Link>
              <p className="mt-4 max-w-sm text-sm leading-6 text-dust">
                We set up and run AI receptionists, websites and chatbots for Australian trades and service businesses. Custom software for everyone else.
              </p>
              <p className="mt-4 text-sm text-dust">
                Based in {siteConfig.location}. Serving clients {siteConfig.serviceArea}.
              </p>
              <div className="mt-4 grid gap-1 text-sm text-dust">
                <a href={`mailto:${siteConfig.email}`} className="hover:text-gold-light">{siteConfig.email}</a>
                {siteConfig.phone ? <a href={`tel:${siteConfig.phone.replace(/\s+/g, '')}`} className="hover:text-gold-light">{siteConfig.phone}</a> : null}
                {siteConfig.demoPhone ? <a href={`tel:${siteConfig.demoPhone.replace(/\s+/g, '')}`} className="hover:text-gold-light">Demo: {siteConfig.demoPhone}</a> : null}
              </div>
            </div>

            <div>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-ash">Services</p>
              <div className="mt-3 grid gap-2 text-sm">
                {serviceLinks.map((link) => <Link key={link.to} to={link.to} className="text-dust hover:text-gold-light">{link.label}</Link>)}
              </div>
            </div>

            <div>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-ash">Industries</p>
              <div className="mt-3 grid gap-2 text-sm">
                {industryLinks.map((link) => <Link key={link.to} to={link.to} className="text-dust hover:text-gold-light">{link.label}</Link>)}
              </div>
            </div>

            <div>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-ash">Company</p>
              <div className="mt-3 grid gap-2 text-sm">
                {companyLinks.map((link) => <Link key={link.to} to={link.to} className="text-dust hover:text-gold-light">{link.label}</Link>)}
                <Link to="/custom-software" className="text-dust hover:text-gold-light">Custom Software</Link>
                <Link to="/pricing" className="text-dust hover:text-gold-light">Pricing</Link>
                <Link to="/work" className="text-dust hover:text-gold-light">Work</Link>
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-col gap-6 border-t border-white/[0.06] pt-6 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-4 text-xs text-ash">
              {legalLinks.map((link) => <Link key={link.to} to={link.to} className="hover:text-gold-light">{link.label}</Link>)}
              <a href={siteConfig.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-gold-light">LinkedIn</a>
              <a href={siteConfig.bulletchat} target="_blank" rel="noopener noreferrer" className="hover:text-gold-light">BulletChat</a>
            </div>
            <p className="text-xs leading-5 text-ash">
              {legalLine} © {new Date().getFullYear()} CloudNext. All rights reserved.
            </p>
          </div>
        </Container>
      </footer>
    </div>
  );
}
